import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import API_BASE_URL from "config/api";
import { useTranslation } from "react-i18next";

import LinearProgress from "@mui/material/LinearProgress";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

function RefreshStatus({
  publicId,
  enabled,
  intervalMs,
  onSuccess,
  labels,
  byUser,
  username,
  userPk,
}) {
  const { t } = useTranslation();

  const mergedLabels = useMemo(
    () => ({
      title: labels?.title ?? t("payments.refresh.title"),
      init: labels?.init ?? t("payments.refresh.init"),
      errorPrefix: labels?.errorPrefix ?? t("payments.refresh.errorPrefix"),
    }),
    [labels, t]
  );

  const [checking, setChecking] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  const endpoint = useMemo(() => {
    const base = String(API_BASE_URL || "").replace(/\/$/, "");
    return byUser ? `${base}/web2/payment/status-by-user/` : `${base}/web2/payment/status/`;
  }, [byUser]);

  const getCookie = (name) => {
    if (typeof document === "undefined") return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const fetchOnce = async () => {
    if (!publicId) return null;

    const csrftoken = getCookie("csrftoken");
    const payload = byUser
      ? {
          public_id: publicId,
          ...(username ? { username } : {}),
          ...(userPk !== null && userPk !== undefined && userPk !== ""
            ? { user_pk: Number(userPk) }
            : {}),
        }
      : { public_id: publicId };

    const res = await axios.post(endpoint, payload, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        ...(!byUser && csrftoken ? { "X-CSRFToken": csrftoken } : {}),
      },
    });

    return res?.data || null;
  };

  const statusLine = useMemo(() => {
    if (!info || info.is_topuped) return null;
    const statusValue = info.status_display || info.status;
    if (statusValue === undefined || statusValue === null || statusValue === "") return null;

    // If translation contains interpolation placeholder, use it; otherwise fall back to prefix + value.
    const tpl = labels?.status ?? t("payments.refresh.status");
    if (typeof tpl === "string" && tpl.indexOf("{{status") !== -1) {
      return t("payments.refresh.status", { status: statusValue });
    }
    return `${tpl}: ${statusValue}`;
  }, [info, labels, t]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!publicId) return undefined;

    let stopped = false;
    let timer = null;

    const tick = async () => {
      if (stopped) return;

      try {
        setChecking(true);
        setError(null);

        const data = await fetchOnce();
        if (stopped) return;

        if (!data) {
          setError(mergedLabels.errorPrefix);
          return;
        }

        if (!data.ok) {
          setError(data.error || mergedLabels.errorPrefix);
          return;
        }

        setInfo(data);

        if (data.is_topuped) {
          stopped = true;
          setChecking(false);
          if (timer) clearInterval(timer);
          if (onSuccess) onSuccess(data);
        }
      } catch (e) {
        if (!stopped) setError(e?.message || mergedLabels.errorPrefix);
      } finally {
        if (!stopped) setChecking(false);
      }
    };

    // check immediately
    tick();
    // then poll
    timer = setInterval(tick, intervalMs);

    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
    };
  }, [publicId, enabled, intervalMs, mergedLabels.errorPrefix, onSuccess]);

  if (!enabled || !publicId) return null;

  return (
    <MKBox mt={2}>
      <MKTypography variant="caption" color="text" display="block" mb={0.75}>
        {mergedLabels.title}
      </MKTypography>

      <MKBox display="flex" justifyContent="center" mt={0.5}>
        <MKBox
          sx={{
            width: "75%",
            overflow: "hidden",
            borderRadius: "999px",
          }}
        >
          <LinearProgress
            color="success"
            sx={{
              display: "block",
              // Slow down the indeterminate animation
              "& .MuiLinearProgress-bar1Indeterminate": {
                animationDuration: "3.2s",
              },
              "& .MuiLinearProgress-bar2Indeterminate": {
                animationDuration: "3.2s",
              },
            }}
          />
        </MKBox>
      </MKBox>

      <MKBox mt={1}>
        {error && (
          <MKTypography variant="caption" color="error" display="block">
            {error}
          </MKTypography>
        )}

        {checking && !info && !error && (
          <MKTypography variant="caption" color="text" display="block">
            {mergedLabels.init}
          </MKTypography>
        )}

        {statusLine && (
          <MKTypography variant="caption" color="text" display="block">
            {statusLine}
          </MKTypography>
        )}
      </MKBox>
    </MKBox>
  );
}

RefreshStatus.propTypes = {
  publicId: PropTypes.string,
  enabled: PropTypes.bool,
  intervalMs: PropTypes.number,
  onSuccess: PropTypes.func,
  byUser: PropTypes.bool,
  username: PropTypes.string,
  userPk: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labels: PropTypes.shape({
    title: PropTypes.string,
    init: PropTypes.string,
    status: PropTypes.string,
    errorPrefix: PropTypes.string,
  }),
};

RefreshStatus.defaultProps = {
  publicId: "",
  enabled: true,
  intervalMs: 10000,
  onSuccess: null,
  byUser: false,
  username: "",
  userPk: null,
  labels: null,
};

export default RefreshStatus;
